/**
 * TemplateConfigurationTab — the edit form of an existing template (entity-management §3: a standard inline form on the
 * detail page, no assistant by default). Demo page: /form-layouts. Field help is the (?) popover of each field.
 */
import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import {
  createOrUpdateTemplateMutation,
  getTemplateQueryKey,
  listTemplatesQueryKey,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { Card, useToast } from '../../../components/ui/index.js';
import { Form, FormActions, useZodForm } from '../../../components/form/index.js';
import { Can } from '../../../components/auth/Can.js';
import type { AgentTemplate } from '../../../generated/api/types.gen.js';
import {
  TemplateFields,
  formValuesToUpsert,
  templateFormSchema,
  templateToFormValues,
  type TemplateFormValues,
} from '../TemplateForm.js';

export const TemplateConfigurationTab: React.FC<{ template: AgentTemplate }> = ({ template }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const save = useMutation(createOrUpdateTemplateMutation());
  const defaults = templateToFormValues(template);
  const form = useZodForm(templateFormSchema, { defaultValues: defaults });
  const disableModelInvocation = useWatch({ control: form.control, name: 'disableModelInvocation' });

  const submit = async (values: TemplateFormValues) => {
    await save.mutateAsync({ path: { templateSlug: template.slug }, body: formValuesToUpsert(values) });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getTemplateQueryKey({ path: { templateSlug: template.slug } }) }),
      queryClient.invalidateQueries({ queryKey: listTemplatesQueryKey() }),
    ]);
    form.reset(values);
    notify('Template saved');
  };

  return (
    <Can
      policy="CanManageAgentTemplates"
      fallback={
        <Card title="Configuration">
          <p className="text-sm text-muted">You do not have permission to change templates.</p>
        </Card>
      }
    >
      <Card title="Configuration">
        <p className="-mt-3 mb-6 text-sm text-muted">
          Template-owned defaults. Runtime task and workflow contracts can narrow these.
        </p>
        <Form form={form} onSubmit={submit} guardUnsaved className="space-y-8">
          <TemplateFields slugEditable={false} disableModelInvocation={disableModelInvocation ?? false} />
          <FormActions submitLabel="Save changes" onCancel={() => form.reset(defaults)} cancelLabel="Reset" />
        </Form>
      </Card>
    </Can>
  );
};
